import { bff, toClientError } from './bff'

const run = (p) => p.then((r) => r.data).catch((e) => Promise.reject(toClientError(e)))

// Transcript library — browse transcribe's channels / videos / transcripts.
export const listChannels = () => run(bff.get('/api/import/channels'))

// Expand a query into the words a title might use for the same topic, in any
// language the channel writes in. Returns the terms so the UI can show (and
// let you drop) them — a search that silently reinterprets you is untrustable.
export const expandSearchTerms = ({ query, sampleTitles }) =>
  run(bff.post('/api/import/search-terms', { query, sample_titles: sampleTitles }))

// min/maxMinutes: length window in MINUTES (the unit the UI and the user think
// in). The BFF converts to the seconds the engine stores.
export const listChannelVideos = ({ channelId, filter, limit, offset, sort, search, minMinutes, maxMinutes, terms }) =>
  run(
    bff.get(`/api/import/channels/${encodeURIComponent(channelId)}/videos`, {
      params: {
        filter_status: filter, limit, offset, sort, search: search || undefined,
        min_minutes: minMinutes || undefined, max_minutes: maxMinutes || undefined,
        term: terms?.length ? terms : undefined,
      },
      // axios repeats an array key (?term=a&term=b), which is what the BFF reads.
      paramsSerializer: { indexes: null },
    })
  )

// Index one transcribed channel video into a project (subtitles → timestamped).
export const indexVideo = ({ video, project, artifact }) =>
  run(bff.post('/api/import/videos/index', { video, project, artifact }, { timeout: 0 }))

// Batch indexing — delta preview: how many of a scope's transcribed videos are
// still to index (the rest are already in the project). scope = channel_id or
// category; video_ids is the surgical (explicit rows) mode.
export const indexBatchPreview = ({ project, channel_id, category, artifact, video_ids, reindex }) =>
  run(bff.post('/api/import/transcripts/index-preview',
    { project, channel_id, category, artifact, video_ids, reindex }, { timeout: 120000 }))

// Batch indexing — start the durable saga; returns { saga_id } to poll.
export const indexBatchStart = ({ project, channel_id, category, artifact, video_ids, reindex }) =>
  run(bff.post('/api/import/transcripts/index-batch',
    { project, channel_id, category, artifact, video_ids, reindex }, { timeout: 60000 }))

// Bulk transcription (plan-transcribe-parity Step 1) — delta preview: how many
// videos under a scope (channel_id | category), narrowed by an optional
// [from,to] year range + title search, are not yet transcribed.
export const transcribeBatchPreview = ({ channel_id, category, years, search }) =>
  run(bff.post('/api/import/transcripts/transcribe-preview',
    { channel_id, category, years, search }, { timeout: 120000 }))

// Bulk transcription — start the durable saga (one video at a time). Returns
// { saga_id } to poll; stop with cancelSaga.
export const transcribeBatchStart = ({ project, channel_id, category, years, search, options }) =>
  run(bff.post('/api/import/transcripts/transcribe-batch',
    { project, channel_id, category, years, search, options }, { timeout: 60000 }))

// Stop a running saga (bulk transcription honours this between videos).
export const cancelSaga = (id) => run(bff.post(`/api/sagas/${id}/cancel`))

// F3.1 — find channels/podcasts covering a topic (weighted by popularity,
// with an AI-consolidated shortlist).
export const discoverChannels = ({ query, n, project }) =>
  run(bff.post('/api/discover/channels', { query, n, project }, { timeout: 0 }))

// F3.2 — item-level discovery: videos (yt/rutube) + subscribed feed items.
export const discoverItems = ({ query, n, sources }) =>
  run(bff.post('/api/discover', { query, n, sources }, { timeout: 0 }))

// F3.3 — mined runs are worth returning to.
export const listDiscoveryHistory = (limit = 20) =>
  run(bff.get('/api/discover/history', { params: { limit } }))
export const getDiscoveryRun = (id) => run(bff.get(`/api/discover/history/${id}`))
export const deleteDiscoveryRun = (id) => run(bff.delete(`/api/discover/history/${id}`))

export const getTranscript = ({ video, format }) =>
  run(bff.get('/api/import/transcript', { params: { video, format } }))

// Register a new channel (fetch its video metadata). Slow for big channels —
// no client timeout; the BFF caps it server-side.
export const addChannel = ({ source, channel_id, max_videos }) =>
  run(bff.post('/api/import/channels', { source, channel_id, max_videos }, { timeout: 0 }))

// TikTok sign-in for captcha-walled profiles: opens a controlled browser for
// the user to log in; blocks until sign-in or timeout. No client timeout.
export const tiktokLogin = ({ timeout_s, refresh } = {}) =>
  run(bff.post('/api/import/channels/tiktok/login', { timeout_s, refresh }, { timeout: 0 }))

// Multichannel: videos across every channel of a category.
export const listCategoryVideos = ({ category, filter, limit, offset, sort, search, minMinutes, maxMinutes, terms }) =>
  run(
    bff.get(`/api/import/categories/${encodeURIComponent(category)}/videos`, {
      params: {
        filter_status: filter, limit, offset, sort, search: search || undefined,
        min_minutes: minMinutes || undefined, max_minutes: maxMinutes || undefined,
        term: terms?.length ? terms : undefined,
      },
      // axios repeats an array key (?term=a&term=b), which is what the BFF reads.
      paramsSerializer: { indexes: null },
    })
  )

export const setChannelCategory = ({ channel, category }) =>
  run(bff.put(`/api/import/channels/${encodeURIComponent(channel)}/category`, { category }))

// Followed channels: periodic auto-refresh + optional captions-only
// auto-transcribe of new arrivals; "N new since last visit" badges.
export const listFollows = () => run(bff.get('/api/import/follows'))
export const setFollow = ({ channel, followed, auto_transcribe, source, channel_id, video_count,
  project, backfill, backfill_limit, backfill_since }) =>
  run(bff.put(`/api/import/channels/${encodeURIComponent(channel)}/follow`, {
    followed, auto_transcribe, source, channel_id, video_count,
    project, backfill, backfill_limit, backfill_since,
  }))
export const markChannelSeen = ({ channel, video_count }) =>
  run(bff.post(`/api/import/channels/${encodeURIComponent(channel)}/seen`, { video_count }))
